import { TenantPaymentsSettings } from "@/types/payments";
import { LocalStorageKeys } from "../../login/hooks/useLocalStorage";
import { tenantAdminService } from "@/services/tenant-admin-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { is404 } from "@/utils/httpErrors";

/**
 * Query keys
 */
export const tenantPaymentsSettingsKeys = {
  all: ["tenantPaymentSettings"] as const,

  paymentSettings: () =>
    [...tenantPaymentsSettingsKeys.all, "paymentSettings"] as const,
};

/**
 * GET: tenant payment settings
 */
export const useGetTenantPaymentSettings = (enabled: boolean = true) => {
  const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);

  return useQuery<TenantPaymentsSettings | null>({
    queryKey: tenantPaymentsSettingsKeys.paymentSettings(),
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      try {
        return await tenantAdminService.getTenantPaymentSettings(tenantId!);
      } catch (e) {
        if (is404(e)) return null;

        throw e;
      }
    },

    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

/**
 * PATCH: update hotel payment settings (and invalidate status if your backend status depends on settings)
 */
export const useUpdateTenantPaymentSettings = () => {
  const tenantId = localStorage.getItem(LocalStorageKeys.TENANT_ID);
  const qc = useQueryClient();

  return useMutation<void, unknown, { paymentsEnabled: boolean }>({
    mutationFn: async (payload) => {
      if (!tenantId) throw new Error("tenantId is required");
      return tenantAdminService.updateTenantPaymentSettings(tenantId, payload);
    },
    onSuccess: () => {
      if (!tenantId) return;
      qc.invalidateQueries({
        queryKey: tenantPaymentsSettingsKeys.paymentSettings(),
      });
    },
  });
};
